import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LoginBannerRow {
  id: string;
  tag: string | null;
  title: string;
  highlight: string | null;
  description: string;
  image_url: string | null;
  cta_label: string | null;
  cta_href: string | null;
  sort_order: number;
  is_active: boolean;
}

const COLUMNS = 'id, tag, title, highlight, description, image_url, cta_label, cta_href, sort_order, is_active';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const table = () => (supabase as any).from('platform_login_banners');

/** Banners ativos para a tela de login (público). */
export function useActiveLoginBanners() {
  return useQuery({
    queryKey: ['login-banners', 'active'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<LoginBannerRow[]> => {
      const { data, error } = await table().select(COLUMNS).eq('is_active', true).order('sort_order');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useAdminLoginBanners() {
  return useQuery({
    queryKey: ['login-banners', 'admin'],
    queryFn: async (): Promise<LoginBannerRow[]> => {
      const { data, error } = await table().select(COLUMNS).order('sort_order');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useLoginBannerMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['login-banners'] });

  const save = useMutation({
    mutationFn: async (b: Partial<LoginBannerRow> & { title: string }) => {
      const { id, ...rest } = b;
      const q = id ? table().update(rest).eq('id', id) : table().insert(rest);
      const { data, error } = await q.select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('Sem permissão para salvar.');
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await table().delete().eq('id', id).select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('Sem permissão para excluir.');
    },
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      for (const it of items) {
        const { error } = await table().update({ sort_order: it.sort_order }).eq('id', it.id);
        if (error) throw error;
      }
    },
    onSuccess: invalidate,
  });

  return { save, remove, reorder };
}

export async function uploadLoginBannerImage(file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `login-banners/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from('brand-assets').upload(path, file, { upsert: false, contentType: file.type });
  if (error) throw error;
  return supabase.storage.from('brand-assets').getPublicUrl(path).data.publicUrl;
}
